import { ErrorHandler, Provider } from '@angular/core';

import { createErrorHandler, init as sentryAngularInit } from '@sentry/angular';
import { init as sentryCapacitorInit } from '@sentry/capacitor';
import type { Event } from '@sentry/capacitor';
import { environment } from '@ts/shared/util-environment';

import { fingerprintApiErrorEvent } from './fingerprint-api-error-event.util';

// Browser-extension code throwing inside our zone gets filed as our error (#11321).
// Unanchored: Capacitor's RewriteFrames stores frames as `app:///chrome-extension://…`.
const BROWSER_EXTENSION_URL_PATTERNS: RegExp[] = [
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari(-web)?-extension:\/\//i,
];

/**
 * Total `beforeSend`: drops status-0 `Unknown API error` events and fingerprints
 * the real 4xx/5xx by endpoint (see {@link fingerprintApiErrorEvent}), returning
 * every other event untouched. Wrapped so a bug here can never throw — a throwing
 * `beforeSend` would silently drop unrelated events — always defaulting to
 * returning the original event.
 */
function beforeSendApiError<T extends Event>(event: T): T | null {
  try {
    return fingerprintApiErrorEvent(event);
  } catch {
    return event;
  }
}

export function initializeSentryProvider(): readonly Provider[] {
  const additionalProviders: Provider[] = [];

  if (environment.sentryDsn) {
    // Init by passing the sibling SDK's init as the second parameter.
    sentryCapacitorInit(
      {
        dsn: environment.sentryDsn,
        tracesSampleRate: environment.sentryTracesSampleRate,
        beforeSend: beforeSendApiError,
        denyUrls: BROWSER_EXTENSION_URL_PATTERNS,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sentryAngularInit as any,
    );
    additionalProviders.push({
      provide: ErrorHandler,
      useValue: createErrorHandler(),
    });
  }
  return additionalProviders;
}
